import { type Static, Type } from '@sinclair/typebox';
import { ArangoMetadataSchema } from './arango-metadata';
import { AttributeSchema } from './attribute.js';
import {
  CriteriasSchema,
  FilterSchema,
  NullableSchema,
  PaginatedSchema,
  SavingPercentageRangeSchema,
  SwapTypeAcceptanceSchema,
} from './common.js';

export const ProductMetricSchema = Type.Object({
  name: Type.String(),
  _id: Type.String(),
  subtype: Type.Optional(Type.String()),
  value: NullableSchema(Type.Number()),
  unit: Type.Optional(Type.String()),
  description: Type.Optional(Type.String()),
});
export type ProductMetricDTO = Static<typeof ProductMetricSchema>;

export const ProductSchema = Type.Composite(
  [
    ArangoMetadataSchema,
    Type.Object({
      attributes: Type.Array(AttributeSchema),
      embed_vector: Type.Array(Type.Number()),
      item_description_english: Type.String(),
      item_description_local_language: Type.String(),
      months: Type.Array(Type.Number()),
      packs_vals: Type.Array(Type.Number()),
      possible_volumes: Type.Array(
        Type.Object({
          unit: Type.String(),
          value: Type.Number(),
        }),
      ),
      supplied_item_id: Type.Union([Type.String(), Type.Number()]),
      supplier_item_code: Type.String(),
      supplier_id: Type.Number(),
      spend_vals: Type.Array(Type.Number()),
      swap_info: Type.Optional(
        Type.Object({
          option_number: Type.Number(),
          pack_volume_unit: Type.String(),
          pro_rated_pack_volume: Type.Number(),
          savings: NullableSchema(Type.Number()),
          similarity: Type.Number(),
          swap_rule: SwapTypeAcceptanceSchema,
          swap_metrics: Type.Array(ProductMetricSchema),
        }),
      ),
      tagged_description: Type.String(),
      taxons: Type.Array(
        Type.Object({
          _id: Type.String(),
        }),
      ),
      url: Type.Optional(Type.String()),
      url_image: Type.Optional(Type.String()),
      vector: Type.Array(Type.Number()),
      volume_vals: Type.Array(Type.Number()),
      product_costs: Type.Array(ProductMetricSchema),
      product_transaction_data: Type.Array(ProductMetricSchema),
    }),
  ],
  { additionalProperties: false },
);

// Partial because no field is guaranteed to be present if using query fields to pick some fields
export type ProductDTO = Partial<Static<typeof ProductSchema>>;

export const DefaultLightProductSchema = Type.Omit(ProductSchema, [
  'embed_vector',
  'item_description_english',
  'months',
  'packs_vals',
  'spend_vals',
  'vector',
]);

export const PaginatedSwapsSchema = Type.Intersect([
  PaginatedSchema(
    Type.Object({
      product: Type.Partial(ProductSchema),
      swaps: Type.Array(Type.Partial(ProductSchema)),
    }),
  ),
  Type.Object({
    sumOfTotalVolumePacks: Type.Number(),
    sumOfSavingsOfBestOptions: NullableSchema(Type.Number()),
    updatedVolumesFlag: Type.Optional(Type.Boolean()),
  }),
]);
export type PaginatedSwapsDTO = Static<typeof PaginatedSwapsSchema>;

// =====================================================================================================================
// Retrieve Product
// =====================================================================================================================

export const RetrieveProductParamsSchema = Type.Object({
  productId: Type.String(),
});
export type RetrieveProductParams = Static<typeof RetrieveProductParamsSchema>;

export const RetrieveProductBodySchema = Type.Object({
  criterias: CriteriasSchema,
  // If true, the full product will be returned. If false, a lighter version is returned.
  fullProduct: Type.Optional(Type.Boolean()),
  fromDate: Type.Optional(Type.Number()),
  toDate: Type.Optional(Type.Number()),
});
export type RetrieveProductBody = Static<typeof RetrieveProductBodySchema>;

// =====================================================================================================================
// List Products
// =====================================================================================================================

export const ListProductsBodySchema = Type.Object({
  leftFilter: FilterSchema,
  countOnly: Type.Optional(Type.Boolean()),
  // Impacts only the possible swap options (the right products). When passed, each swap candidate must have volumes over the last validSwapMonthsThreshold.
  // If it is 5, for example, it means we need to check the swap candidate volume for the last 5 months is positive. If it is not, the product is not eligible as a swap option.
  // This flag is only used by the swap API and is configured by the Data Engineers.
  validSwapMonthsThreshold: Type.Optional(Type.Number()),
  // Restricts the products to only those keys
  productKeys: Type.Optional(Type.Array(Type.String())),
  limit: Type.Optional(Type.Number()),
  offset: Type.Optional(Type.Number()),
  taxonsIds: Type.Optional(Type.Array(Type.String())),
  suppliersNamesForTaxons: Type.Optional(Type.Array(Type.String())),
});
export type ListProductsBody = Static<typeof ListProductsBodySchema>;

export const ListProductsResponsePaginatedSchema = PaginatedSchema(ProductSchema);
export type ListProductsResponsePaginated = Static<typeof ListProductsResponsePaginatedSchema>;

export const ListProductsResponseSchema = Type.Union([
  Type.Number(),
  ListProductsResponsePaginatedSchema,
  Type.Array(Type.Partial(ProductSchema)),
]);
export type ListProductsResponse = Static<typeof ListProductsResponseSchema>;

export const ProductsAndSuppliersMetadataSchema = Type.Object({
  total: Type.Number(),
  productCodes: Type.Array(Type.String()),
  supplierIDs: Type.Array(Type.String()),
  hashes: Type.Array(Type.String()),
  listOfSuppliersAndProducts: Type.Array(
    Type.Object({
      supplier_id: Type.Number(),
      supplier_name: Type.String(),
      product_code: Type.String(),
      item_description_local_language: Type.String(),
    }),
  ),
});
export type ProductsAndSuppliersMetadata = Static<typeof ProductsAndSuppliersMetadataSchema>;

// =====================================================================================================================
// Swap explanation
// =====================================================================================================================

export const SwapExplanationBodySchema = Type.Object({ product: ProductSchema, swap: ProductSchema });
export type SwapExplanationBody = Static<typeof SwapExplanationBodySchema>;

export enum SwapExplanationSwapFilter {
  LIKE_FOR_LIKE = 0,
  SIMPLE = 1,
  ALL_FEASIBLE_DISCARD_COUNTRY = 2,
  ALL_FEASIBLE = 3,
  LIKE_FOR_LIKE_SUPPLIER = 4,
  ALL_FEASIBLE_SUPPLIER = 5,
}

const SwapExplanationSwapFilterEnumSchema = Type.Enum(SwapExplanationSwapFilter);

export enum SwapExplanationCriteriaResult {
  EXACT_MATCH = 1,
  UNKNOWN = 2,
  UNCONSTRAINED = 3,
  SUBSET = 4,
  RANGE = 5,
  UNSUPPORTED = 6,
}

const SwapExplanationCriteriaResultTypeSchema = Type.Enum(SwapExplanationCriteriaResult);

const SwapExplanationCriteriaSchema = Type.Object({
  attribute: Type.String(),
  original_val: Type.Any(),
  result_type: SwapExplanationCriteriaResultTypeSchema,
  candidate_val: Type.Any(),
  original_min_delta: Type.Number(),
  original_max_delta: Type.Number(),
});
export type SwapExplanationCriteria = Static<typeof SwapExplanationCriteriaSchema>;

export const SwapExplanationSchema = Type.Object({
  matched_rule: SwapExplanationSwapFilterEnumSchema,
  rejected_rule: SwapExplanationSwapFilterEnumSchema,
  unknown_values: Type.Record(Type.String(), Type.Array(Type.String())),
  matched_criteria: Type.Array(SwapExplanationCriteriaSchema),
  unmatched_criteria: Type.Array(SwapExplanationCriteriaSchema),
  supplier_deprioritized: Type.Boolean(),
});
export type SwapExplanation = Static<typeof SwapExplanationSchema>;

export const SwapExplanationResponseSchema = Type.Object({
  matched: Type.Optional(SwapExplanationSchema),
  rejected: Type.Optional(SwapExplanationSchema),
});
export type SwapExplanationResponse = Static<typeof SwapExplanationResponseSchema>;

// =====================================================================================================================
// List Swaps
// =====================================================================================================================

export const ListSwapsBodySchema = Type.Object({
  fields: Type.Object({
    products: Type.String(),
    swaps: Type.String(),
  }),
  leftFilters: Type.Object({
    productFilters: FilterSchema,
    swapFilters: FilterSchema,
  }),
  limit: Type.Optional(NullableSchema(Type.Number())),
  offset: Type.Optional(NullableSchema(Type.Number())),
  onlyPositiveSavings: Type.Optional(Type.Boolean()),
  productId: Type.Optional(Type.String()),
  // Optional list of product key and swap option key to restrict the swaps to
  restrictTo: Type.Optional(
    Type.Array(
      Type.Object({
        productKey: Type.String(),
        swapKey: Type.String(),
      }),
    ),
  ),
  sameSupplier: Type.Optional(Type.Boolean()),
  similarityThreshold: Type.Number({ default: 70, minimum: 0, maximum: 100 }),
  simulatedPrice: Type.Optional(Type.Number()),
  sortBy: Type.Union([
    Type.Literal('SAVINGS'),
    Type.Literal('SIMILARITY'),
    Type.Literal('OPTION_NUMBER'),
    Type.Literal('SAVINGS_PERCENTAGE'),
  ]),
  savingsPercentageRange: Type.Optional(SavingPercentageRangeSchema),
  statsOnly: Type.Optional(Type.Boolean()),
  swapTypeAcceptance: ProductSchema.properties.swap_info.properties.swap_rule,
  validSwapMonthsThreshold: Type.Optional(Type.Number()),
});
export type ListSwapsBody = Static<typeof ListSwapsBodySchema>;

export enum ProductCosts {
  GrossPrice = 'product_costs/gross_price_per_pack',
  GrossPricePerPortion = 'product_costs/gross_price_per_pack_per_portions',
  GrossPricePerKg = 'product_costs/gross_price_per_pack_per_kg',
  GrossPricePerL = 'product_costs/gross_price_per_pack_per_l',
  BidPrice = 'product_costs/bid_price',
  BidPricePerPortion = 'product_costs/bid_price_per_portions',
  BidPricePerKg = 'product_costs/bid_price_per_kg',
  BidPricePerL = 'product_costs/bid_price_per_l',
  AdjustedPrice = 'product_costs/adjusted_price',
  AdjustedPricePerPortion = 'product_costs/adjusted_price_per_portions',
  AdjustedPricePerKg = 'product_costs/adjusted_price_per_kg',
  AdjustedPricePerL = 'product_costs/adjusted_price_per_l',
  AverageDeliveryPricePerPortion = 'product_costs/average_delivery_price_per_pack_per_portions',
  AverageDeliveryPricePerKg = 'product_costs/average_delivery_price_per_pack_per_kg',
  AverageDeliveryPricePerL = 'product_costs/average_delivery_price_per_pack_per_l',
  AverageDeliveryPrice = 'product_costs/average_delivery_price_per_pack',
  LogisticCost = 'product_costs/logistic_cost',
  LogisticCostPerPortion = 'product_costs/logistic_cost_per_portions',
  LogisticCostPerKg = 'product_costs/logistic_cost_per_kg',
  LogisticCostPerL = 'product_costs/logistic_cost_per_l',
  VDACost = 'product_costs/VDA_cost',
  VDACostPerPortion = 'product_costs/VDA_cost_per_portions',
  VDACostPerKg = 'product_costs/VDA_cost_per_kg',
  VDACostPerL = 'product_costs/VDA_cost_per_l',
}

export enum SwapMetrics {
  Savings = 'swap_metrics/savings',
  SavingsPercentage = 'swap_metrics/savings_percentage',
  NominativeInflationImpact = 'swap_metrics/nominative_inflation_impact',
}

export enum ProductTransactionData {
  TotalVolumePacks = 'product_transaction_data/total_volume_packs',
  TotalVolumeMetricSystem = 'product_transaction_data/total_volume_metric_system',
  TotalDeliverySpendTargetCurrency = 'product_transaction_data/total_delivery_spend_target_currency',
}
